import { useEffect, useState } from 'react';
import type { BillDTO, BillStatus, BillsSummaryResponse, Page } from '@six-pockets/shared';
import { billsApi } from '@/api';
import { SearchIcon } from '@/components/icons';
import { PageHeader } from '@/components/layout/PageHeader';
import { DatePicker, InlineAlert, Pagination, SelectField, TextField, type SelectOption } from '@/components/ui';
import { BillDetailsModal } from '@/features/bills/components/BillDetailsModal';
import { BillRow } from '@/features/bills/components/BillRow';
import { RevenueCards } from '@/features/bills/components/RevenueCards';
import { useAuth } from '@/context/AuthContext';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { todayKey } from '@/utils/date';
import styles from '@/styles/pages/BillsPage/style.scss';

const PAGE_SIZE = 12;

/** The status filter; 'all' sends no status (and is the only place waived bills show). */
type StatusFilter = 'all' | Extract<BillStatus, 'settled' | 'partial' | 'balance'>;

const STATUS_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'All bills' },
  { value: 'settled', label: 'Settled' },
  { value: 'partial', label: 'Partial' },
  { value: 'balance', label: 'Balance' },
];

const messageOf = (err: unknown) => (err instanceof Error ? err.message : 'Something went wrong');

/**
 * Bills home: every bill made on a day (today by default), newest first, with a status filter, search (bill number,
 * customer, or a name on the bill) and paging, all done by the server. View opens the full bill. On top, the day's
 * table-time revenue (gross and net); for a login without Reports (the club manager) the figures stay hidden until
 * tapped, then show for 3 seconds.
 */
export function BillsPage() {
  const [status, setStatus] = useState<StatusFilter>('all');
  const [date, setDate] = useState(todayKey);
  const [search, setSearch] = useState('');
  const query = useDebouncedValue(search.trim(), 300);
  const [page, setPage] = useState(1);

  const [result, setResult] = useState<Page<BillDTO> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<BillDTO | null>(null);
  const [summary, setSummary] = useState<BillsSummaryResponse | null>(null);
  const masked = !useAuth().can('reports');

  // A new search goes back to the first page.
  useEffect(() => setPage(1), [query]);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    billsApi.list({ date, status: status === 'all' ? undefined : status, q: query || undefined, page, pageSize: PAGE_SIZE }, ctrl.signal).then(
      (r) => {
        if (r.items.length === 0 && r.page > 1) setPage(r.page - 1);
        else setResult(r);
        setLoading(false);
      },
      (err) => {
        if (ctrl.signal.aborted) return;
        setError(messageOf(err));
        setLoading(false);
      },
    );
    return () => ctrl.abort();
  }, [date, status, query, page]);

  // The cards follow the day only, not the status filter or search.
  useEffect(() => {
    const ctrl = new AbortController();
    billsApi.summary({ date }, ctrl.signal).then(setSummary, (err) => {
      if (!ctrl.signal.aborted) setError(messageOf(err));
    });
    return () => ctrl.abort();
  }, [date]);

  const changeStatus = (s: StatusFilter) => {
    setStatus(s);
    setPage(1);
  };

  const changeDate = (d: string) => {
    setDate(d);
    setPage(1);
  };

  const bills = result?.items ?? [];

  return (
    <>
      <PageHeader title="Bills" />

      {summary && <RevenueCards summary={summary} masked={masked} />}

      <div className={styles.toolbar}>
        <div className={styles.filters}>
          <SelectField size="md" aria-label="Bill status" options={STATUS_OPTIONS} value={status} onChange={(v) => changeStatus(v as StatusFilter)} />
        </div>
        <div className={styles.tools}>
          <TextField
            size="md"
            aria-label="Search bills by number, customer or name"
            placeholder="Search bill #, customer, mobile"
            prefix={<SearchIcon size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.search}
          />
          <div className={styles.date}>
            <DatePicker label="Day billed" value={date} onChange={changeDate} />
          </div>
        </div>
      </div>

      {error && <InlineAlert message={error} onDismiss={() => setError(null)} />}

      {!result && loading ? (
        <p className={styles.empty}>Loading bills…</p>
      ) : bills.length === 0 ? (
        <p className={styles.empty}>{query ? 'No bills match your search' : status === 'all' ? 'No bills on this day' : `No ${status} bills on this day`}</p>
      ) : (
        <ul className={styles.list} aria-busy={loading}>
          {bills.map((b) => (
            <BillRow key={b.id} bill={b} onView={setViewing} />
          ))}
        </ul>
      )}

      {result && <Pagination page={result.page} pageSize={result.pageSize} total={result.total} onPageChange={setPage} />}

      {viewing && <BillDetailsModal key={viewing.id} bill={viewing} onClose={() => setViewing(null)} />}
    </>
  );
}
